/**
 * Los atajos de la pantalla de inicio.
 *
 * Under the big button the home screen had two tiles, and what they were changed with the day:
 * «Cerrar la caja» or «Liquidar la ruta», then «Llegó mercadería», or «Lo que vendí» when there
 * was room. Everything else was two or three taps further: «Todo lo demás», a section, often a
 * tab. And the owner's working screens (a purchase, an expense, a new product, the payroll, the
 * books) are the ones opened every day. So the owner asked for them on the home screen
 * (2026-10-02): nine, in three rows of three, writing something down first, then prices and
 * people, then stock and the books.
 *
 * Twelve at first. Reponer, Control and Efectivo came off again, at the owner's say: each has an
 * alert on the home screen that appears when it needs somebody, and opens it, so its tile was a
 * second door beside the first. A cashier's and a driver's «Lo que vendí» went the same way, a
 * second door to the sales «Vendido hoy» already opens. «Historial de ventas» is not that door:
 * it is every sale, not today's, the owner's tile since 2026-10-08 and a cashier's since 2026-10-09.
 * Reponer and Control came back for the owner on 2026-10-16, with «Bitácora», as a row of their
 * own (the owner: «another row of buttons for the owner between the row that has "Credit, Orders,
 * Sales History" and "Inventory, Analytics, and Accounting/Payroll". This new row should be
 * "Reorder, Control, and Log"»): the alerts still open them when something needs somebody, and the
 * tiles whenever the owner wants to look. Twelve again, in four rows of three.
 *
 * These are places, so they hold still. The till's own next step, «Terminar mi turno», comes and
 * goes with the till and is a bar of its own above them (`Inicio.tsx`): in
 * the grid it would move every tile after it one place each time the till opened or closed, and
 * a grid somebody uses every day is found by where things are.
 *
 * A cashier and a driver get theirs, the same way: a cashier six since the owner gave them the
 * goods too (2026-10-09), a driver two. A shortcut whose screen the person could not open is left
 * out, which the roles below never need, but a role that changes later must not get a door that
 * only refuses. A tile wears its section's colour, the one
 * «Todo lo demás» gives that section, so the two say the same thing about where a screen lives.
 * And each says what it is for, in a bubble when a mouse rests on it (`ui/Globo.tsx`).
 */

import type { Permission, Role } from '@abastece/shared';
import type { Clave } from '../i18n/index.js';
import type { LadoGlobo } from '../ui/Globo.js';
import type { NombreIcono, Tinte } from '../ui/Iconos.js';
import { pantallaPermitida } from './permisos.js';
import { seccionDe } from './secciones.js';

/** The screens a shortcut can open. */
export type DestinoAtajo =
  | '/compras/nueva'
  | '/gastos'
  | '/productos'
  | '/clientes'
  | '/pedidos'
  | '/ventas'
  | '/inventario'
  | '/fiado'
  | '/contabilidad'
  | '/analisis'
  | '/recargas'
  | '/reponer'
  | '/control'
  | '/bitacora';

export interface Atajo {
  etiqueta: Clave;
  /** What it is for, in a sentence: the bubble a mouse resting on it brings up. */
  ayuda: Clave;
  destino: DestinoAtajo;
  icono: NombreIcono;
  /** Makes a new one, so its chip carries a +: a purchase and an expense, on the first row. */
  mas?: true;
  /** Opens the screen with its form for a new one already open, not on its list: «Gastos». */
  abreFormulario?: true;
}

export const ATAJOS: Readonly<Record<Role, readonly Atajo[]>> = {
  dueno: [
    // Writing something down, and what there is to sell: what was bought, what went out, the
    // products. A purchase wears Compras' bag rather than «Recibir»'s box, which has a + drawn into
    // it: with the corner's, it had two. «Nuevo gasto» and «Nuevo producto» until the owner asked
    // for «Gastos» and «Productos» (2026-10-05): the expense still opens on its form, the products
    // on their list, where «Nuevo» is.
    {
      etiqueta: 'inicio.mercaderiaComprada',
      ayuda: 'inicio.ayudaCompra',
      destino: '/compras/nueva',
      icono: 'compras',
      mas: true,
    },
    {
      etiqueta: 'inicio.gastos',
      ayuda: 'inicio.ayudaGasto',
      destino: '/gastos',
      icono: 'gastos',
      mas: true,
      abreFormulario: true,
    },
    { etiqueta: 'productos.titulo', ayuda: 'inicio.ayudaProductos', destino: '/productos', icono: 'productos' },
    // Who owes, who ordered, what sold. Prices took the clients' place (2026-10-06), and the
    // fiado took the prices' (the owner, 2026-10-07: «instead of the prices button can we get a
    // "Credit" button»): prices stay a tab of «Productos y precios». «Pedidos y reparto» until the
    // owner took the trucks out (2026-10-04): the orders are all there is to it now. «Planilla»
    // was the third until the owner put the sales in its place (2026-10-08: «replace the Payroll
    // button on the home screen with "Sales History"»): all of them, where «Vendido hoy» below
    // opens today's. The payroll is a tab beside «Contabilidad», which says so on its tile.
    { etiqueta: 'porCobrar.titulo', ayuda: 'inicio.ayudaFiado', destino: '/fiado', icono: 'fiado' },
    { etiqueta: 'pedidos.titulo', ayuda: 'inicio.ayudaPedidos', destino: '/pedidos', icono: 'pedidos' },
    { etiqueta: 'inicio.historialVentas', ayuda: 'inicio.ayudaHistorialVentas', destino: '/ventas', icono: 'ventas' },
    // What to keep an eye on (the owner, 2026-10-16: «Reorder, Control, and Log»): what is running
    // out and what to order, what each person did with the money, and everything recorded. Named as
    // the tab of «Reponer, compras y proveedores» is, as «Todo lo demás» names «Control», and as the
    // log calls itself. The log is the app's own page, at the foot of «Todo lo demás» with no
    // section, so its chip has no colour of one.
    { etiqueta: 'seccion.reponer', ayuda: 'inicio.ayudaReponer', destino: '/reponer', icono: 'reponer' },
    { etiqueta: 'control.titulo', ayuda: 'inicio.ayudaControl', destino: '/control', icono: 'control' },
    { etiqueta: 'bitacora.titulo', ayuda: 'inicio.ayudaBitacora', destino: '/bitacora', icono: 'bitacora' },
    // Stock and the books: what there is, how the shop is doing (`routes/Analisis.tsx`, in the
    // place prices left), and whether it is making money, with the payroll a tab away.
    { etiqueta: 'inv.titulo', ayuda: 'inicio.ayudaInventario', destino: '/inventario', icono: 'inventario' },
    { etiqueta: 'analisis.titulo', ayuda: 'inicio.ayudaAnalisis', destino: '/analisis', icono: 'analisis' },
    { etiqueta: 'inicio.contabilidadPlanilla', ayuda: 'inicio.ayudaContabilidad', destino: '/contabilidad', icono: 'ganancia' },
  ],
  cajero: [
    // What the clients owe, what they ordered (the owner, 2026-10-08: «Credit, Orders, and Clients in
    // that order»), and the sales where the clients were (2026-10-09: «replace the Clients button
    // with the Sales History button»): the row the owner has, the sales all of them. «Recargas» led
    // the row until 2026-10-08, and stays a tab of «Ventas, caja y recargas»; the clients stay a tab
    // of «Clientes, fiado y pedidos», both in «Todo lo demás».
    { etiqueta: 'porCobrar.titulo', ayuda: 'inicio.ayudaFiado', destino: '/fiado', icono: 'fiado' },
    { etiqueta: 'pedidos.titulo', ayuda: 'inicio.ayudaPedidos', destino: '/pedidos', icono: 'pedidos' },
    { etiqueta: 'inicio.historialVentas', ayuda: 'inicio.ayudaHistorialVentas', destino: '/ventas', icono: 'ventas' },
    // And under them the goods, in the owner's order (2026-10-09: «"Bought Goods", "Inventory", and
    // "Products"»): the owner's own three tiles, the purchase on its form with its +.
    {
      etiqueta: 'inicio.mercaderiaComprada',
      ayuda: 'inicio.ayudaCompra',
      destino: '/compras/nueva',
      icono: 'compras',
      mas: true,
    },
    { etiqueta: 'inv.titulo', ayuda: 'inicio.ayudaInventario', destino: '/inventario', icono: 'inventario' },
    { etiqueta: 'productos.titulo', ayuda: 'inicio.ayudaProductos', destino: '/productos', icono: 'productos' },
  ],
  repartidor: [
    { etiqueta: 'inicio.pedidosDeHoy', ayuda: 'inicio.ayudaPedidosDeHoy', destino: '/pedidos', icono: 'pedidos' },
    { etiqueta: 'clientes.titulo', ayuda: 'inicio.ayudaClientes', destino: '/clientes', icono: 'clientes' },
  ],
};

/** Somebody's shortcuts: their role's, less any screen they could not open. */
export function atajosPara(rol: Role, puede: (p: Permission) => boolean): Atajo[] {
  return ATAJOS[rol].filter((a) => pantallaPermitida(a.destino, puede));
}

/** The colour of the section the shortcut's screen is in; none for the log, which is in none. */
export const tinteDe = (a: Atajo): Tinte => seccionDe(a.destino)?.tinte ?? 'neutro';

/**
 * Rows that come out even: four as two by two, two side by side, otherwise three to a row. Three
 * still fit on a 320 px phone, where the tiles are tighter (`Inicio.tsx`), so the owner's three
 * rows stay the rows they asked for on any phone.
 */
const columnas = (n: number): 2 | 3 => (n === 4 || n <= 2 ? 2 : 3);

export const columnasPara = (n: number): string => (columnas(n) === 2 ? 'grid-cols-2' : 'grid-cols-3');

/**
 * Where a tile's bubble lines up: from the tile's left edge in the first column and its right edge
 * in the last, so a bubble wider than its tile never hangs off the side of the screen.
 */
export function ladoDelGlobo(i: number, n: number): LadoGlobo {
  const c = columnas(n);
  const columna = i % c;
  return columna === 0 ? 'izquierda' : columna === c - 1 ? 'derecha' : 'centro';
}
